// A small XML reader and writer for the parts of an .xlsx. It reads
// elements, attributes, text and CDATA; it skips comments and processing
// instructions and refuses a DOCTYPE (no entity expansion). Element names
// lose their namespace prefix ("x:c" -> "c"); attribute names keep it
// ("r:id").

export interface XmlNode {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  text: string; // the text directly inside (concatenated)
}

export class XmlError extends Error {}

const MAX_DEPTH = 256;

function decodeEntities(s: string): string {
  if (!s.includes("&")) return s;
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|lt|gt|amp|quot|apos);/g, (_, e: string) => {
    switch (e) {
      case "lt":
        return "<";
      case "gt":
        return ">";
      case "amp":
        return "&";
      case "quot":
        return '"';
      case "apos":
        return "'";
    }
    const cp = e[1] === "x" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : "";
  });
}

function localName(name: string): string {
  const i = name.indexOf(":");
  return i < 0 ? name : name.slice(i + 1);
}

const ATTR = /([^\s=/>]+)\s*=\s*("([^"]*)"|'([^']*)')/g;

export function parseXml(src: string): XmlNode {
  const root: XmlNode = { name: "#root", attrs: {}, children: [], text: "" };
  const stack: XmlNode[] = [root];
  const top = (): XmlNode => stack[stack.length - 1] ?? root;
  let i = 0;
  const n = src.length;
  while (i < n) {
    const lt = src.indexOf("<", i);
    if (lt < 0) {
      top().text += decodeEntities(src.slice(i));
      break;
    }
    if (lt > i) top().text += decodeEntities(src.slice(i, lt));
    if (src.startsWith("<!--", lt)) {
      const end = src.indexOf("-->", lt + 4);
      if (end < 0) throw new XmlError("unterminated comment");
      i = end + 3;
    } else if (src.startsWith("<![CDATA[", lt)) {
      const end = src.indexOf("]]>", lt + 9);
      if (end < 0) throw new XmlError("unterminated CDATA");
      top().text += src.slice(lt + 9, end);
      i = end + 3;
    } else if (src.startsWith("<?", lt)) {
      const end = src.indexOf("?>", lt + 2);
      if (end < 0) throw new XmlError("unterminated processing instruction");
      i = end + 2;
    } else if (src.startsWith("<!", lt)) {
      throw new XmlError("DOCTYPE is not allowed");
    } else if (src[lt + 1] === "/") {
      const end = src.indexOf(">", lt);
      if (end < 0) throw new XmlError("unterminated end tag");
      const name = localName(src.slice(lt + 2, end).trim());
      const open = stack.pop();
      if (open === undefined || stack.length === 0 || open.name !== name) throw new XmlError(`mismatched </${name}>`);
      i = end + 1;
    } else {
      // find the tag's end outside quotes
      let j = lt + 1;
      let quote = "";
      for (; j < n; j++) {
        const c = src[j];
        if (quote) {
          if (c === quote) quote = "";
        } else if (c === '"' || c === "'") quote = c;
        else if (c === ">") break;
      }
      if (j >= n) throw new XmlError("unterminated tag");
      const selfClosing = src[j - 1] === "/";
      const body = src.slice(lt + 1, selfClosing ? j - 1 : j);
      const sp = body.search(/[\s]/);
      const rawName = sp < 0 ? body : body.slice(0, sp);
      const node: XmlNode = { name: localName(rawName), attrs: {}, children: [], text: "" };
      if (sp >= 0) {
        const rest = body.slice(sp);
        ATTR.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = ATTR.exec(rest)) !== null) if (m[1] !== undefined) node.attrs[m[1]] = decodeEntities(m[3] ?? m[4] ?? "");
      }
      top().children.push(node);
      if (!selfClosing) {
        if (stack.length > MAX_DEPTH) throw new XmlError("too deep");
        stack.push(node);
      }
      i = j + 1;
    }
  }
  if (stack.length !== 1) throw new XmlError("unclosed elements");
  const doc = root.children[0];
  if (doc === undefined) throw new XmlError("empty document");
  return doc;
}

export function child(node: XmlNode | undefined, name: string): XmlNode | undefined {
  return node?.children.find((c) => c.name === name);
}

export function children(node: XmlNode | undefined, name: string): XmlNode[] {
  return node === undefined ? [] : node.children.filter((c) => c.name === name);
}

/** All the text inside, at any depth (for rich text runs: <si><r><t>..) */
export function deepText(node: XmlNode): string {
  if (node.name === "t") return node.text;
  if (node.name === "rPh" || node.name === "phoneticPr") return ""; // furigana
  let s = "";
  for (const c of node.children) s += deepText(c);
  return s;
}

// ---- writing ----

// characters XML 1.0 does not allow, written as _xHHHH_ like Excel
// eslint-disable-next-line no-control-regex
const BAD_XML = /[\u0000-\u0008\u000b\u000c\u000e-\u001f￾￿]/g;

export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(BAD_XML, (c) => `_x${c.charCodeAt(0).toString(16).padStart(4, "0").toUpperCase()}_`);
}

export const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
